import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Bookmark, BookmarkCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import PageHeader from "@/components/PageHeader";
import { STEP_FORWARD_ACTIONS } from "@/lib/stepForwardData";
import { getSavedActions, toggleSavedAction } from "@/lib/storage";
import { STEP } from "@/constants/testIds";

export default function StepForward() {
  const [savedIds, setSavedIds] = useState(
    new Set(getSavedActions().map((a) => a.id))
  );

  useEffect(() => {
    setSavedIds(new Set(getSavedActions().map((a) => a.id)));
  }, []);

  const actions = useMemo(() => STEP_FORWARD_ACTIONS, []);

  const grouped = useMemo(() => {
    const map = {};
    actions.forEach((a) => {
      map[a.category] = map[a.category] || [];
      map[a.category].push(a);
    });
    return map;
  }, [actions]);

  const handleToggleSave = (action) => {
    const isSaved = toggleSavedAction(action);
    setSavedIds(new Set(getSavedActions().map((a) => a.id)));
    toast.success(
      isSaved ? "Saved." : "Removed from saved.",
      isSaved
        ? { description: "Find it in your Saved dashboard." }
        : undefined
    );
  };

  return (
    <PageContainer testid={STEP.root}>
      <PageHeader
        title="Step Forward"
        subtitle="Small, repeatable practices. Choose what fits; ignore the rest."
        variant="back"
        exitLabel="Back"
        testid={STEP.exit}
      />

      {Object.entries(grouped).map(([group, items], gi) => (
        <motion.div
          key={group}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: gi * 0.06 }}
          className="mb-7"
        >
          <p
            className="text-[11px] uppercase tracking-[0.3em] mb-3 px-1"
            style={{ color: "var(--un-terracotta)", fontFamily: "Manrope, sans-serif" }}
          >
            {group}
          </p>

          <div className="flex flex-col gap-3">
            {items.map((a, idx) => {
              const saved = savedIds.has(a.id);
              return (
                <div
                  key={a.id}
                  data-testid={`${STEP.actionPrefix}${a.id}`}
                  className="un-card p-5"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3
                      className="text-[17px] font-medium leading-snug"
                      style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
                    >
                      {a.title}
                    </h3>
                    <button
                      data-testid={`${STEP.saveActionPrefix}${a.id}`}
                      onClick={() => handleToggleSave(a)}
                      aria-label={saved ? "Unsave" : "Save"}
                      className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center un-press"
                      style={{
                        background: saved
                          ? "rgba(74,32,64,0.10)"
                          : "rgba(74,32,64,0.04)",
                        color: saved
                          ? "var(--un-plum)"
                          : "var(--un-text-3)",
                      }}
                    >
                      {saved ? (
                        <BookmarkCheck size={16} />
                      ) : (
                        <Bookmark size={16} />
                      )}
                    </button>
                  </div>
                  <p
                    className="text-[14px] leading-relaxed mb-3"
                    style={{ color: "var(--un-text-2)" }}
                  >
                    {a.body}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <Clock
                      size={12}
                      style={{ color: "var(--un-text-3)" }}
                    />
                    <span
                      className="text-[11px] font-medium tracking-wide"
                      style={{
                        color: "var(--un-text-3)",
                        fontFamily: "Manrope, sans-serif",
                      }}
                    >
                      ~{a.minutes} min
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      ))}

      <div className="h-6" />
    </PageContainer>
  );
}
